import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useIdleTimer } from 'hooks/useIdleTimer';
import { logout } from 'store/actions/auth-actions';
import { IApplicationState } from 'store/state/app-state';
import { Routing } from 'shared/constants/routing';
import { SESSION_TIMEOUT_CONFIG } from 'shared/constants/session-timeout';
import SessionTimeoutModal from './SessionTimeoutModal';

const SessionTimeoutManager: React.FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const authState = useSelector((state: IApplicationState) => state.UserData);
    const isAuthenticated = Boolean(authState && authState?.authConfigs && authState?.authConfigs?.accessToken);

    const [isPromptOpen, setIsPromptOpen] = React.useState(false);
    const [remainingSeconds, setRemainingSeconds] = React.useState(
        SESSION_TIMEOUT_CONFIG.WARNING_BEFORE_TIMEOUT_MS / 1000,
    );
    const countdownIntervalRef = React.useRef<ReturnType<typeof setInterval>>();

    const clearCountdown = React.useCallback(() => {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    }, []);

    const handleForceLogout = React.useCallback(async () => {
        clearCountdown();
        setIsPromptOpen(false);
        await dispatch(logout());
        history.push(Routing.LogIn);
    }, [dispatch, history, clearCountdown]);

    const handlePrompt = React.useCallback(() => {
        setRemainingSeconds(SESSION_TIMEOUT_CONFIG.WARNING_BEFORE_TIMEOUT_MS / 1000);
        setIsPromptOpen(true);

        clearCountdown();
        countdownIntervalRef.current = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev <= 1) {
                    clearCountdown();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [clearCountdown]);

    const handleActive = React.useCallback(() => {
        clearCountdown();
        setIsPromptOpen(false);
    }, [clearCountdown]);

    const { resetTimer } = useIdleTimer({
        isActive: isAuthenticated,
        onIdle: handleForceLogout,
        onPrompt: handlePrompt,
        onActive: handleActive,
    });

    React.useEffect(() => clearCountdown, [clearCountdown]);

    if (!isAuthenticated) return null;

    return (
        <SessionTimeoutModal
            open={isPromptOpen}
            remainingSeconds={remainingSeconds}
            onStayLoggedIn={resetTimer}
            onLogoutNow={handleForceLogout}
        />
    );
};

export default SessionTimeoutManager;
